import { FormControl, FormsModule, NgControl, ReactiveFormsModule, AbstractControl, ValidatorFn, Validators, FormGroupDirective, NgForm } from '@angular/forms';
import { Component, Input, OnChanges, OnInit, Self, SimpleChanges } from '@angular/core';
import { ControlBaseComponent } from '../control-base/control-base.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Hospital, LookUp } from '../../../models/look-up.model';
import { StateService } from '../../../services/state.service';
import { MatInputModule } from '@angular/material/input';
import { map, Observable, startWith } from 'rxjs';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'minster-autocomplete',
    standalone: true,
    imports: [FormsModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, ReactiveFormsModule, AsyncPipe],
    templateUrl: './minster-autocomplete.component.html',
    styleUrl: './minster-autocomplete.component.css'
})

export class MinsterAutocompleteComponent extends ControlBaseComponent<number> implements OnInit, OnChanges {
    @Input('sort') _sort: boolean = false;

    myControl = new FormControl<string | LookUp>('');
    filteredOptions?: Observable<LookUp[]>;

    _lookUps: Hospital[] = [];
    _visibleLookUps: Hospital[] = [];
    _forceValidation: boolean = true;

    constructor(@Self() public controlDir: NgControl, stateService: StateService) {
        super(controlDir, stateService);
    }

    ngOnInit(): void {
        this.setup();
        this.populateLookUps();
        const myValidators: ValidatorFn[] = [];

        this.filteredOptions = this.myControl.valueChanges.pipe(
            startWith(''),
            map(value => {
                const name = typeof value === 'string' ? value : value?.description;
                return name ? this._filter(name as string) : this._lookUps.slice();
            }),
        );

        if (this._required) {
            myValidators.push((control: AbstractControl) => nullValidator(control));
            myValidators.push(Validators.required);
        }

        const control = this.controlDir.control;
        if (control) {
            control.setValidators(myValidators);
            control.updateValueAndValidity();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this._disabled || this._readOnly) {
            this.myControl.disable();
        }
        else {
            this.myControl.enable();
        }
    }

    override writeValue(value: any): void {
        this._value = value;

        if (value) {
            const theHospitalLookUp = this._lookUps.find(x => x.hospitalId == this._value);
            this.myControl.setValue(theHospitalLookUp!);
        }
        else {
            this.myControl.setValue(null);
        }
    }

    @Input() set lookUp(value: Hospital[]) {
        if (value) {
            this._lookUps = value;
        } 
        else {
            this._lookUps = [];
        }

        this.populateLookUps();
    }

    populateLookUps() {
        if (this._sort) {
            this.sortLookups();
        }
        this._visibleLookUps = this._lookUps;
    }

    sortLookups() {
        this._lookUps = this._lookUps.sort((a, b) => a.description.localeCompare(b.description));
    }

    _filter(name: string): LookUp[] {
        const filterValue = name.toLowerCase();
        const filterOptions = this._lookUps.filter(option => option.description!.toLowerCase().includes(filterValue));

        if (filterOptions.length == 0) {
            this._value = 0;
            this._propagateChange(this._value);
            this._showValidationError = true;
            this.myControl.setErrors({ required: true });
        } else {
            this._showValidationError = false;
            this.myControl.setErrors(null);
        }

        return filterOptions;
    }

    displayFn(lookUp: LookUp): string {
        return lookUp ? lookUp.description! : '';
    }

    onValueUpdated(event: any): void {
        this._value = event.source.value.hospitalId;
        this._propagateChange(this._value);
    }

    checkValue() {
        if (this.myControl.value === '') {
            this._value = 0;
            this._propagateChange(this._value);
            this._showValidationError = true;
            this.myControl.setErrors({ required: true });
        } else {
            this._showValidationError = false;
            this.myControl.setErrors(null);
        }
    }
}

function nullValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value;
    const isInvalid = (value === 0 || value === undefined);
    return isInvalid ? { required: true } : null;
}
